import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Settings, 
  LogOut, 
  Menu,
  X,
  Repeat,
  BarChart3,
  Package,
  Megaphone,
  ShieldCheck
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useState } from 'react';
import SupportChat from './SupportChat';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const todayStr = new Date().toDateString();
  const [promoBannerDismissedOn, setPromoBannerDismissedOn] = useState(
    sessionStorage.getItem('promo_expiry_banner_dismissed')
  );
  const [showAfterInfo, setShowAfterInfo] = useState(false);
  
  const promoDaysLeft = user?.comp_plan_expires_at && !user?.subscription_current_period_end
    ? Math.ceil((new Date(user.comp_plan_expires_at).getTime() - Date.now()) / 86400000)
    : null;
  // Dismissible per session; inside the final 7 days it reappears daily
  const promoBannerDismissed = promoDaysLeft !== null && promoDaysLeft <= 7
    ? promoBannerDismissedOn === todayStr
    : !!promoBannerDismissedOn;
  const showPromoBanner = !promoBannerDismissed && promoDaysLeft !== null && promoDaysLeft >= 0 && promoDaysLeft <= 30;
  const compPlanName = user?.subscription_plan
    ? user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1)
    : 'Pro';
  
  const dismissPromoBanner = () => {
    sessionStorage.setItem('promo_expiry_banner_dismissed', todayStr);
    setPromoBannerDismissedOn(todayStr);
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isPaidUser = user?.subscription_plan && user.subscription_plan !== 'free';

  const navItems = [
    { icon: <LayoutDashboard className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <FileText className="w-5 h-5" />, label: 'Invoices', path: '/invoices' },
    { icon: <Repeat className="w-5 h-5" />, label: 'Recurring', path: '/recurring' },
    { icon: <Package className="w-5 h-5" />, label: 'Products', path: '/products' },
    { icon: <Megaphone className="w-5 h-5" />, label: 'Marketing', path: '/marketing', paid: true },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Reports', path: '/reports', paid: true },
    { icon: <Users className="w-5 h-5" />, label: 'Clients', path: '/clients' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
    ...(user?.is_admin ? [{ icon: <ShieldCheck className="w-5 h-5" />, label: 'Admin', path: '/admin' }] : []),
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 flex-col border-r bg-card">
        <div className="p-6">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
            <Logo />
          </Link>
        </div>

        <nav className="flex-1 px-4">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-md transition-colors duration-200',
                    location.pathname === item.path
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b">
        <div className="flex items-center justify-between p-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Logo size="sm" wordmarkClassName="text-base" />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-card border-b shadow-lg animate-slide-down">
            <nav className="p-4">
              <ul className="space-y-1">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        'flex items-center gap-3 px-4 py-3 rounded-md transition-colors duration-200',
                        location.pathname === item.path
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted'
                      )}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                <Button variant="ghost" className="gap-2" onClick={handleLogout}>
                  <LogOut className="w-5 h-5" />
                  Logout
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:pt-0 pt-16">
        {showPromoBanner && (
          <div
            className="bg-amber-100 border-b border-amber-300 px-4 py-3"
            data-testid="promo-expiry-banner"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <p className="text-sm text-amber-900 flex-1">
                <strong>Your {compPlanName} access ends in {promoDaysLeft} {promoDaysLeft === 1 ? 'day' : 'days'}.</strong>{' '}
                <button
                  onClick={() => setShowAfterInfo(!showAfterInfo)}
                  className="underline text-amber-700 hover:text-amber-900"
                  data-testid="promo-banner-what-happens-link"
                >
                  What happens after?
                </button>
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" onClick={() => navigate('/settings')} data-testid="promo-banner-subscribe-btn">
                  Keep {compPlanName}
                </Button>
                <button
                  onClick={dismissPromoBanner}
                  className="p-1 rounded hover:bg-amber-200 text-amber-900"
                  aria-label="Dismiss"
                  data-testid="promo-banner-dismiss-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {showAfterInfo && (
              <p className="text-xs text-amber-800 mt-2" data-testid="promo-banner-after-info">
                You'll move to the Free plan (2 invoices/month) and keep all of your invoices, clients, and data. You can upgrade again anytime.
              </p>
            )}
          </div>
        )}
        {children}
      </main>
      
      {/* Support Chat Widget */}
      <SupportChat />
    </div>
  );
};

export default Layout;
